import React, { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { ArrowLeftIcon } from '@heroicons/react/solid';
import useGet from "../../hooks/read/useGet";
import toast from 'react-hot-toast';
import usePut from "../../hooks/usePut";
import Moment from "react-moment";
import spinner from '../../assets/images/spinner.gif';

const EditAttendanceRecord = ({ open, onClose, attendanceId }) => {
  const [formData, setFormData] = useState({
    present_days: '',
    absent_days: '',
    paid_leaves: '',
  });
  const [year, setYear] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);

  const { list: attendanceList } = useGet({
    url: "employee_yearly_attendance_data",
    initialData: {
      employee_yearly_attendance_id: attendanceId
    },
    onSuccess: (response) => {
      const data = response.data[0];
      setFormData({
        present_days: data.present_days,
        absent_days: data.absent_days,
        paid_leaves: data.paid_leave,
      });
      setYear(data.year);
      setFromDate(data.from_date);
      setToDate(data.to_date);
    },
    onError: () => {
      toast.error("Error fetching attendance record");
    },
  });

  const { handleAuth: updateAttendance } = usePut({
    url: "update_employee_yearly_attendance",
    refreshUrl: '/employee_yearly_attendance_list',
    onSuccess: () => {
      toast.success('Attendance record updated successfully');
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Error updating attendance record");
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const totalDays = parseInt(formData.present_days) + parseInt(formData.absent_days);
    if (totalDays > 365) {
      toast.error("The total days cannot exceed 365.");
      return;
    }

    try {
      setLoading(true);
      await updateAttendance.mutateAsync({
        employee_yearly_attendance_id: attendanceId,
        present_days: formData.present_days,
        absent_days: formData.absent_days,
        paid_leaves: formData.paid_leaves
      });
    } catch (error) {
      console.error("Error updating attendance record:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor={'right'} open={open} onClose={onClose}>
      <div className='h-full bg-white px-8' style={{ width: 'calc(100vw - 250px)' }}>
        {attendanceList.isLoading || attendanceList.isFetching ? (
          <div className='h-full flex items-center justify-center'>
            <img src={spinner} alt="Loading..." className='w-10 h-10' />
          </div>
        ) : (
          <form className='space-y-8' onSubmit={handleSubmit}>
            <div className='flex items-center justify-between  py-5 border-b border-gray-200 sticky z-40 top-0 bg-white'>
              <div className='flex items-center space-x-3'>
                <ArrowLeftIcon className=' text-gray-700 w-8 h-8 cursor-pointer' onClick={onClose} />
                <p className='text-gray-700 text-3xl font-semibold'>Edit Attendance Record</p>
              </div>
              <button type="submit" className=' bg-color1 px-5 py-2 text-lg text-white rounded-md' disabled={loading}>
                <div className='flex items-center justify-center space-x-2'>
                  <span>{loading ? 'Updating...' : 'Update Record'}</span>
                </div>
              </button>
            </div>
            <div className=''>
              <div className='grid grid-cols-3 gap-5 py-5'>
                <div>
                  <div className=''>
                    <label className='label'>Year: </label>
                    <input
                      type="text"
                      value={year}
                      readOnly
                      className='input bg-gray-100'
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <label className='label'>From Date: </label>
                    <div className='input bg-gray-100'>
                      <Moment format="ll">{fromDate * 1000}</Moment>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <label className='label'>To Date: </label>
                    <div className='input bg-gray-100'>
                      <Moment format="ll">{toDate * 1000}</Moment>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <label className='label'>Present Days: </label>
                    <input
                      type="number"
                      name="present_days"
                      value={formData.present_days}
                      onChange={handleInputChange}
                      required={true}
                      className='input'
                      placeholder='Enter Present Days'
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <label className='label'>Absent Days: </label>
                    <input
                      type="number"
                      name="absent_days"
                      value={formData.absent_days}
                      onChange={handleInputChange}
                      required={true}
                      className='input'
                      placeholder='Enter Absent Days'
                    />
                  </div>
                </div>
                <div>
                  <div>
                    <label className='label'>Paid Leaves: </label>
                    <input
                      type="number"
                      name="paid_leaves"
                      value={formData.paid_leaves}
                      onChange={handleInputChange}
                      required={true}
                      className='input'
                      placeholder='Enter Paid Leaves'
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </Drawer>
  );
};

export default EditAttendanceRecord;
